/**
 * A write the agent asks for, as a line of the thread (2026-10-12; it was a
 * card fixed above the box, and one not seen in five minutes was lost). It
 * shows what changes, and takes the answer while the request is open — while
 * a run waits, and for seven days after. A yes makes the write on the server;
 * one the server could not make (`stale`: the row changed since; `refused`)
 * says why. The summary opens where the data is shown: the server names it.
 * An answer the server did not take says why too (#63): a 500 left the card
 * as it was, and the Accept seemed to do nothing.
 */
import { useState } from "react";

import { api } from "../api/client";
import { ApiError, ok, reason } from "../api/query";
import { useLayout } from "../shell/useLayout";
import type { Request as Asked } from "./fold";
import styles from "./Request.module.css";
import { useChat } from "./store";

/** `modify_db`'s `updates`, as field → new value, and the value it replaces. */
function changes(request: Asked): { key: string; from?: unknown; to: unknown }[] {
  const updates = request.params.updates;
  if (typeof updates !== "object" || updates === null) return [];
  const before = request.before ?? {};
  return Object.entries(updates).map(([key, to]) => ({
    key,
    to,
    ...(key in before ? { from: before[key] } : {}),
  }));
}

const show = (value: unknown) =>
  value === null || value === undefined
    ? "∅"
    : typeof value === "object"
      ? JSON.stringify(value)
      : String(value);

const SETTLED: Record<Exclude<Asked["status"], "pending">, string> = {
  confirmed: "Accepted — done",
  rejected: "Rejected — nothing was changed",
  expired: "Not answered in 7 days — nothing was changed",
  stale: "Not done",
  refused: "Not done",
};

/** What became of a request, in words; an open one waits. */
export function stateWords(status: Asked["status"], error: string | null): string {
  if (status === "pending") return "Waits for your answer";
  return SETTLED[status] + (error ? `: ${error}` : "");
}

export function Request({ request }: { request: Asked }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // A Drive file to the bin (`drive_trash`) is a delete too.
  const deleting = request.kind === "delete_db" || request.kind === "drive_trash";
  const open = request.status === "pending";
  const fields = changes(request);

  const answer = async (action: "confirm" | "reject") => {
    setBusy(true);
    setFailed(null);
    try {
      const answered = await ok(
        api.POST("/api/agent/confirmation/{confirmation_id}/{action}", {
          params: { path: { confirmation_id: request.id, action } },
        }),
      );
      useChat.getState().resolved(request.id, answered.status, answered.error ?? null);
    } catch (e) {
      // Answered on another screen, or expired: `confirmation_resolved` comes
      // with the 409 and closes the card. Anything else stays here, with why.
      if (!(e instanceof ApiError && e.status === 409))
        setFailed(`Your answer was not taken: ${reason(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const where = request.opens;
  return (
    <div
      className={[styles.card, deleting && styles.delete, !open && styles.settled]
        .filter(Boolean)
        .join(" ")}
      role="group"
      aria-label={deleting ? "A delete the agent asks for" : "A change the agent asks for"}
    >
      <div className={styles.head}>
        <span className={styles.badge}>{deleting ? "Delete" : "Change"}</span>
        {where ? (
          <button
            className={styles.summary}
            title="Show this data"
            onClick={() =>
              useLayout
                .getState()
                .open({ name: where.widget, tab: where.tab, params: where.params ?? null })
            }
          >
            {request.summary}
          </button>
        ) : (
          <span className={styles.summary}>{request.summary}</span>
        )}
      </div>
      {fields.length > 0 && (
        <div className={styles.diff}>
          {fields.map((change) => (
            <div key={change.key} className={styles.row}>
              <span className={styles.key}>{change.key}</span>
              {"from" in change && (
                <>
                  <span className={styles.from}>{show(change.from)}</span>
                  <span className={styles.arrow}>→</span>
                </>
              )}
              <span className={styles.to}>{show(change.to)}</span>
            </div>
          ))}
        </div>
      )}
      {open ? (
        <div className={styles.actions}>
          <button className={styles.reject} onClick={() => void answer("reject")} disabled={busy}>
            Reject
          </button>
          <button className={styles.confirm} onClick={() => void answer("confirm")} disabled={busy}>
            {busy ? "…" : deleting ? "Delete" : "Accept"}
          </button>
        </div>
      ) : (
        <div className={styles.state} role="status">
          {stateWords(request.status, request.error ?? null)}
        </div>
      )}
      {open && failed && (
        <div className={styles.failed} role="alert">
          {failed}
        </div>
      )}
    </div>
  );
}
